<template>
  <Layout>
    <div class="container-fluid">
      <div class="row">
        <div class="col-12">
          <div class="page-title-box d-flex align-items-center justify-content-between">
            <div>
              <h4 class="mb-0">Gateway de Pagamento</h4>
              <p class="text-muted mb-0">Configure pagamentos, métodos e taxas do evento</p>
            </div>
            <!-- Status toggle -->
            <div class="d-flex align-items-center">
              <span class="mr-3 font-size-14" :class="form.payment_enabled ? 'text-success' : 'text-danger'">
                <i :class="form.payment_enabled ? 'bx bx-check-circle' : 'bx bx-x-circle'" class="mr-1"></i>
                {{ form.payment_enabled ? 'Pagamentos Ativos' : 'Pagamentos Inativos' }}
              </span>
              <div class="custom-control custom-switch custom-switch-lg">
                <input type="checkbox" class="custom-control-input" id="payment-master-switch" v-model="form.payment_enabled">
                <label class="custom-control-label" for="payment-master-switch"></label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <loading :active.sync="loading" color="#556ee6" loader="dots" :can-cancel="false" :is-full-page="false" />

      <!-- Ambiente -->
      <div class="row mb-3">
        <div class="col-12">
          <div class="d-flex align-items-center p-3 rounded" :style="appEnvironment === 'production' ? 'background: #d1fae5' : 'background: #fef3c7'">
            <i :class="appEnvironment === 'production' ? 'bx bx-check-shield text-success' : 'bx bx-test-tube text-warning'" class="font-size-24 mr-3"></i>
            <div>
              <strong class="font-size-14">Ambiente: {{ appEnvironment === 'production' ? 'Produção' : 'Sandbox' }}</strong>
              <small class="d-block text-muted">Definido na configuração do servidor. Para alterar, entre em contato com o suporte.</small>
            </div>
          </div>
        </div>
      </div>

      <form @submit.prevent="save">
        <div class="row">
          <!-- Coluna Esquerda -->
          <div class="col-lg-8">

            <!-- Métodos de Pagamento -->
            <div class="card">
              <div class="card-body">
                <h5 class="card-title mb-4">
                  <i class="bx bx-dollar-circle text-success font-size-20 align-middle mr-2"></i>
                  Métodos de Pagamento
                </h5>

                <!-- PIX -->
                <div class="gateway-method-card mb-3 p-3 rounded border" :class="form.pix_enabled ? 'border-success' : ''">
                  <div class="d-flex align-items-center justify-content-between">
                    <div class="d-flex align-items-center">
                      <div class="rounded p-2 mr-3" :style="form.pix_enabled ? 'background: #d1fae5' : 'background: #f3f4f6'">
                        <i class="bx bx-qr font-size-20" :class="form.pix_enabled ? 'text-success' : 'text-muted'"></i>
                      </div>
                      <div>
                        <h6 class="mb-0">
                          PIX
                          <span v-if="form.pix_enabled" class="badge badge-soft-warning ml-2 font-size-11">1,09%</span>
                        </h6>
                        <small class="text-muted">Pagamento instantâneo via QR Code</small>
                      </div>
                    </div>
                    <div class="custom-control custom-switch custom-switch-lg">
                      <input type="checkbox" class="custom-control-input" id="pix-switch" v-model="form.pix_enabled">
                      <label class="custom-control-label" for="pix-switch"></label>
                    </div>
                  </div>
                </div>

                <!-- Boleto -->
                <div class="gateway-method-card mb-3 p-3 rounded border" :class="form.boleto_enabled ? 'border-info' : ''">
                  <div class="d-flex align-items-center justify-content-between">
                    <div class="d-flex align-items-center flex-grow-1">
                      <div class="rounded p-2 mr-3" :style="form.boleto_enabled ? 'background: #dbeafe' : 'background: #f3f4f6'">
                        <i class="bx bx-barcode font-size-20" :class="form.boleto_enabled ? 'text-info' : 'text-muted'"></i>
                      </div>
                      <div class="flex-grow-1">
                        <h6 class="mb-0">
                          Boleto
                          <span v-if="form.boleto_enabled" class="badge badge-soft-info ml-2 font-size-11">R$ 3,19</span>
                        </h6>
                        <small class="text-muted">Pagamento via boleto bancário</small>
                        <div v-if="form.boleto_enabled" class="mt-2">
                          <label class="mb-1 font-size-12 text-muted">Vencimento:</label>
                          <select v-model.number="form.boleto_expiration_days" class="form-control form-control-sm d-inline-block ml-2" style="width: 100px;">
                            <option :value="1">1 dia</option>
                            <option :value="2">2 dias</option>
                            <option :value="3">3 dias</option>
                            <option :value="5">5 dias</option>
                            <option :value="7">7 dias</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    <div class="custom-control custom-switch custom-switch-lg">
                      <input type="checkbox" class="custom-control-input" id="boleto-switch" v-model="form.boleto_enabled">
                      <label class="custom-control-label" for="boleto-switch"></label>
                    </div>
                  </div>
                </div>

                <!-- Cartão de Crédito -->
                <div class="gateway-method-card mb-3 p-3 rounded border" :class="form.credit_card_enabled ? 'border-primary' : ''">
                  <div class="d-flex align-items-center justify-content-between">
                    <div class="d-flex align-items-center flex-grow-1">
                      <div class="rounded p-2 mr-3" :style="form.credit_card_enabled ? 'background: #e0e7ff' : 'background: #f3f4f6'">
                        <i class="bx bx-credit-card font-size-20" :class="form.credit_card_enabled ? 'text-primary' : 'text-muted'"></i>
                      </div>
                      <div class="flex-grow-1">
                        <h6 class="mb-0">
                          Cartão de Crédito
                          <span v-if="form.credit_card_enabled" class="badge badge-soft-primary ml-2 font-size-11">3,19% - 4,99%</span>
                        </h6>
                        <small class="text-muted">Pagamentos com cartão e parcelamento</small>
                        <div v-if="form.credit_card_enabled" class="mt-2">
                          <label class="mb-1 font-size-12 text-muted">Máx. parcelas:</label>
                          <select v-model.number="form.max_installments" class="form-control form-control-sm d-inline-block ml-2" style="width: 80px;">
                            <option v-for="n in 12" :key="n" :value="n">{{ n }}x</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    <div class="custom-control custom-switch custom-switch-lg">
                      <input type="checkbox" class="custom-control-input" id="credit-card-switch" v-model="form.credit_card_enabled">
                      <label class="custom-control-label" for="credit-card-switch"></label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Responsabilidade pelas Taxas -->
            <div class="card">
              <div class="card-body">
                <h5 class="card-title mb-3">
                  <i class="bx bx-transfer-alt text-warning font-size-20 align-middle mr-2"></i>
                  Quem paga as taxas?
                </h5>

                <div class="row">
                  <div class="col-md-6 mb-2">
                    <div
                      class="p-3 border rounded cursor-pointer h-100"
                      :class="form.fee_payer === 'organizer' ? 'border-primary bg-soft-primary' : ''"
                      @click="form.fee_payer = 'organizer'"
                    >
                      <div class="d-flex align-items-start">
                        <div class="custom-control custom-radio mr-2 mt-1">
                          <input type="radio" class="custom-control-input" id="org-pays" value="organizer" v-model="form.fee_payer">
                          <label class="custom-control-label" for="org-pays"></label>
                        </div>
                        <div>
                          <h6 class="mb-1"><i class="bx bx-building mr-1"></i> Organizador</h6>
                          <small class="text-muted">As taxas são descontadas do seu recebimento. O cliente vê o preço limpo.</small>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-6 mb-2">
                    <div
                      class="p-3 border rounded cursor-pointer h-100"
                      :class="form.fee_payer === 'customer' ? 'border-primary bg-soft-primary' : ''"
                      @click="form.fee_payer = 'customer'"
                    >
                      <div class="d-flex align-items-start">
                        <div class="custom-control custom-radio mr-2 mt-1">
                          <input type="radio" class="custom-control-input" id="cust-pays" value="customer" v-model="form.fee_payer">
                          <label class="custom-control-label" for="cust-pays"></label>
                        </div>
                        <div>
                          <h6 class="mb-1"><i class="bx bx-user mr-1"></i> Cliente</h6>
                          <small class="text-muted">O cliente verá "Taxa de serviço" adicionada ao valor no checkout.</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Coluna Direita - Taxas (colapsável) -->
          <div class="col-lg-4">
            <div class="card">
              <div class="card-body p-0">
                <div
                  class="d-flex align-items-center justify-content-between p-3 cursor-pointer"
                  @click="showFees = !showFees"
                >
                  <h5 class="card-title mb-0">
                    <i class="bx bx-receipt text-danger font-size-20 align-middle mr-2"></i>
                    Taxas Pagar.me
                  </h5>
                  <i class="bx font-size-20 text-muted" :class="showFees ? 'bx-chevron-up' : 'bx-chevron-down'"></i>
                </div>

                <div v-show="showFees" class="px-3 pb-3">
                  <!-- Cartão -->
                  <h6 class="text-muted font-size-12 text-uppercase mb-2 mt-2">
                    <i class="bx bx-credit-card mr-1"></i> Cartão de Crédito
                  </h6>
                  <div class="d-flex justify-content-between mb-1 font-size-13">
                    <span>À vista (1x)</span>
                    <strong class="text-primary">3,19%</strong>
                  </div>
                  <div class="d-flex justify-content-between mb-1 font-size-13">
                    <span>Parcelado 2-6x</span>
                    <strong class="text-primary">4,49%</strong>
                  </div>
                  <div class="d-flex justify-content-between mb-3 font-size-13">
                    <span>Parcelado 7-12x</span>
                    <strong class="text-primary">4,99%</strong>
                  </div>

                  <hr class="my-2">

                  <!-- PIX e Boleto -->
                  <h6 class="text-muted font-size-12 text-uppercase mb-2 mt-3">
                    <i class="bx bx-dollar-circle mr-1"></i> PIX e Boleto
                  </h6>
                  <div class="d-flex justify-content-between mb-1 font-size-13">
                    <span>PIX</span>
                    <strong class="text-warning">1,09%</strong>
                  </div>
                  <div class="d-flex justify-content-between mb-1 font-size-13">
                    <span>Boleto</span>
                    <strong class="text-info">R$ 3,19</strong>
                  </div>
                  <div class="d-flex justify-content-between mb-3 font-size-13">
                    <span>Estorno Boleto</span>
                    <strong>R$ 3,19</strong>
                  </div>

                  <hr class="my-2">

                  <!-- Operacional -->
                  <h6 class="text-muted font-size-12 text-uppercase mb-2 mt-3">
                    <i class="bx bx-cog mr-1"></i> Operacional
                  </h6>
                  <div class="d-flex justify-content-between mb-1 font-size-13">
                    <span>Por transação</span>
                    <strong>R$ 0,55</strong>
                  </div>
                  <div class="d-flex justify-content-between mb-1 font-size-13">
                    <span>Antifraude (crédito)</span>
                    <strong>R$ 0,44</strong>
                  </div>
                  <div class="d-flex justify-content-between mb-1 font-size-13">
                    <span>Transferência</span>
                    <strong>R$ 3,67</strong>
                  </div>
                  <div class="d-flex justify-content-between mb-2 font-size-13">
                    <span>Antecipação</span>
                    <strong>2,89%</strong>
                  </div>

                  <div class="alert alert-light mt-3 mb-0 border font-size-12">
                    <i class="bx bx-info-circle mr-1"></i>
                    Taxas do contrato Pagar.me. Para alterações, contate o suporte.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Botão Salvar (sticky) -->
        <div class="row">
          <div class="col-12">
            <div class="card sticky-save-bar">
              <div class="card-body py-3 d-flex align-items-center justify-content-between">
                <div class="d-flex align-items-center">
                  <i class="bx bx-info-circle text-muted font-size-18 mr-2"></i>
                  <span class="text-muted font-size-13">As alterações só terão efeito após salvar.</span>
                </div>
                <button type="submit" class="btn btn-primary px-4" :disabled="loading">
                  <i class="mdi mdi-content-save mr-1"></i>
                  Salvar Configurações
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>

      <!-- Simulador de Vendas (largura total) -->
      <div class="row">
        <div class="col-12">
          <PaymentSimulator :max-installments="form.max_installments" />
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from '@/components/layout/vertical'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import PaymentSimulator from './PaymentSimulator.vue'
import { getPaymentSettings, updatePaymentSettings } from '../recipient/services'

export default {
  components: {
    Layout,
    Loading,
    PaymentSimulator
  },
  data () {
    return {
      loading: false,
      showFees: true,
      appEnvironment: 'sandbox',
      form: {
        pix_enabled: true,
        boleto_enabled: false,
        boleto_expiration_days: 3,
        credit_card_enabled: true,
        max_installments: 12,
        fee_payer: 'organizer',
        payment_enabled: true
      }
    }
  },
  mounted () {
    this.loadSettings()
  },
  methods: {
    async loadSettings () {
      this.loading = true
      try {
        const response = await getPaymentSettings()
        if (response.data) {
          this.appEnvironment = response.data.app_environment || 'sandbox'

          if (response.data.data) {
            const data = response.data.data
            this.form.pix_enabled = data.pix_enabled ?? true
            this.form.boleto_enabled = data.boleto_enabled ?? false
            this.form.boleto_expiration_days = data.boleto_expiration_days ?? 3
            this.form.credit_card_enabled = data.credit_card_enabled ?? true
            this.form.max_installments = data.max_installments ?? 12
            this.form.payment_enabled = data.payment_enabled ?? true
            this.form.fee_payer = data.marketplace_pays_gateway_fee ? 'organizer' : 'customer'
          }
        }
      } catch (error) {
        if (error.response && error.response.status !== 404) {
          this.$toast.error('Erro ao carregar configurações')
          console.error(error)
        }
      } finally {
        this.loading = false
      }
    },
    async save () {
      this.loading = true
      try {
        const payload = {
          payment_enabled: this.form.payment_enabled,
          pix_enabled: this.form.pix_enabled,
          boleto_enabled: this.form.boleto_enabled,
          boleto_expiration_days: this.form.boleto_expiration_days,
          credit_card_enabled: this.form.credit_card_enabled,
          max_installments: this.form.max_installments,
          marketplace_pays_gateway_fee: this.form.fee_payer === 'organizer'
        }

        await updatePaymentSettings(payload)
        this.$toast.success('Configurações salvas com sucesso')
        this.loadSettings()
      } catch (error) {
        this.$toast.error(error.response?.data?.message || 'Erro ao salvar configurações')
        console.error(error)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.bg-soft-primary {
  background-color: rgba(85, 110, 230, 0.1);
}

.cursor-pointer {
  cursor: pointer;
}

.custom-switch-lg .custom-control-label::before {
  height: 1.5rem;
  width: 3rem;
  border-radius: 3rem;
}

.custom-switch-lg .custom-control-label::after {
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 3rem;
}

.custom-switch-lg .custom-control-input:checked ~ .custom-control-label::after {
  transform: translateX(1.5rem);
}

.gateway-method-card {
  transition: all 0.2s ease;
}

.gateway-method-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.sticky-save-bar {
  position: sticky;
  bottom: 0;
  z-index: 10;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.08);
  margin-bottom: 1rem;
}
</style>
